import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, handleLocalStorage } from '@utils';
import { useInterval, useTimer, useNavigate } from '@utils/hooks';

import { useStore, loginActions } from '@modules';

import Alert from '@components/Alert';

import styles from './LogoutNotification.scss';

const cn = bemCssModules(styles, 'LogoutNotification');
const { getItem } = handleLocalStorage;

const LogoutNotification: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [{ login, user }, dispatch] = useStore();
  const { idleTimeout } = login;

  const { counter, isActive, reset, toggle } = useTimer({
    initialCounter: CONST.logoutCountdownTime / 1000,
    startOnMount: false,
  });

  const handleFinally = () => {
    navigate(CONST.nav.loginEmail);
  };

  const logout = () => {
    dispatch(loginActions.logout()).unwrap().finally(handleFinally);
  };

  useEffect(() => {
    if (counter <= 0) logout();
  }, [counter]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const updateIdlTimeout = () => {
    const idleTimestamp = Number(getItem('idle_timestamp'));
    const now = Number(Date.now());
    const timeToShowNotification = idleTimeout * 1000 - CONST.logoutCountdownTime;

    if (!Number.isNaN(idleTimestamp)) {
      const timeDelta = now - idleTimestamp;
      const shouldStartCountDown = timeDelta >= timeToShowNotification;

      if (timeDelta >= idleTimeout * 1000 && isActive) logout();
      if (shouldStartCountDown && !isActive) toggle();
    }
  };

  useInterval({
    callback: updateIdlTimeout,
    shouldStop: !user.data,
    delay: CONST.idleInterval,
  });

  const keepAlive = () => {
    dispatch(loginActions.keepAlive());
    reset();
  };

  if (!isActive) return null;

  return (
    <Alert
      className={cn(null, [className])}
      title={t('notifications.idle_for')}
      content={t('notifications.logout_in', { idle_timeout: idleTimeout / 60, timerVal: counter })}
      buttons={[{ label: t('notifications.continue'), onClick: keepAlive }]}
    />
  );
};

export default LogoutNotification;
